// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
  SheetFooter,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/lib/useIsMobile';
import { useSheetDrag } from '@/lib/useSheetDrag';
import { useResizablePanelWidth } from '@/lib/useResizablePanelWidth';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MOBILE_BREAKPOINT = 640;

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/**
 * Safe HTML/Radix attribute pass-through for the root content element of each
 * GLDrawer surface. Useful for `data-testid`, `aria-*`, and `id`. If
 * `className` is provided it is merged with GLDrawer's own classNames rather
 * than replacing them. GLDrawer-owned behavior (event handlers, style on panel
 * desktop) always wins over these props.
 */
export type GLDrawerContentProps = {
  id?: string;
  className?: string;
  'data-testid'?: string;
} & React.AriaAttributes;

export interface GLDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  /** Visible description rendered below the title. */
  description?: React.ReactNode;
  /**
   * Fallback sr-only description text for Radix a11y when no visible
   * `description` is given. Radix Dialog/Sheet warns without a description.
   */
  srDescription?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * 'panel' — desktop resizable right panel + mobile bottom sheet (default).
   * 'confirm' — desktop centered Dialog + mobile bottom Sheet.
   */
  variant?: 'panel' | 'confirm';
  /** Initial/default panel width (px). Default 440. panel variant only. */
  defaultWidth?: number;
  /** Minimum panel width (px). Default 360. panel variant only. */
  minWidth?: number;
  /** sessionStorage key for persisting panel width. Default 'gl-inspect-panel-width'. panel variant only. */
  storageKey?: string;
  /** Extra classes merged onto the outermost content element. */
  className?: string;
  /** Extra classes merged onto the scrollable body div. */
  bodyClassName?: string;
  /** Extra classes merged onto mobile SheetContent. */
  mobileClassName?: string;
  /** Extra classes merged onto desktop panel/dialog content. */
  desktopClassName?: string;
  /**
   * When this changes the detent drag state resets (pass the currently-open
   * item id so switching entries clears the previous entry's expanded height).
   */
  mobileDragResetKey?: unknown;
  /**
   * When true, all dismissal paths (onOpenChange(false), Escape, interact
   * outside) are blocked. Useful during async in-flight operations.
   */
  preventDismiss?: boolean;
  /**
   * Props spread onto the root content element of each rendered surface.
   * Use for `data-testid`, `aria-*`, and `id`. If `className` is provided it
   * is merged with GLDrawer's own classNames. GLDrawer-owned props (event
   * handlers, style width/maxWidth on panel desktop) always win.
   */
  contentProps?: GLDrawerContentProps;
  /**
   * Viewport width (px) below which the mobile bottom sheet renders. Default 640.
   * Pass 768 on inspectors that follow the page's md collapse so the overlay
   * and page switch layouts together (tables collapse to card lists at md per docs/19).
   */
  mobileBreakpoint?: number;
}

export type GLSheetGrabHandleProps = React.HTMLAttributes<HTMLDivElement>;

export const GLSheetGrabHandle = React.forwardRef<HTMLDivElement, GLSheetGrabHandleProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-testid="sheet-grab-handle"
      className={cn(
        'flex justify-center py-2 cursor-grab active:cursor-grabbing touch-none',
        className,
      )}
      aria-hidden="true"
      {...props}
    >
      <span className="h-1 w-8 rounded-full bg-slate/30" />
    </div>
  ),
);
GLSheetGrabHandle.displayName = 'GLSheetGrabHandle';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GLDrawer({
  open,
  onOpenChange,
  title,
  description,
  srDescription,
  children,
  footer,
  variant = 'panel',
  defaultWidth,
  minWidth,
  storageKey,
  className,
  bodyClassName,
  mobileClassName,
  desktopClassName,
  mobileDragResetKey,
  preventDismiss = false,
  contentProps,
  mobileBreakpoint = MOBILE_BREAKPOINT,
}: GLDrawerProps): React.ReactElement {
  const { className: contentPropsClassName, ...restContentProps } = contentProps ?? {};

  const isMobile = useIsMobile(mobileBreakpoint);

  // Guard all close attempts when preventDismiss is true.
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (preventDismiss && !next) return;
      onOpenChange(next);
    },
    [preventDismiss, onOpenChange],
  );

  // Radix passes native DOM events (KeyboardEvent / Event) — not React SyntheticEvent.
  const onEscapeKeyDown = preventDismiss ? (e: KeyboardEvent) => e.preventDefault() : undefined;
  const onInteractOutside = preventDismiss ? (e: Event) => e.preventDefault() : undefined;

  // -----------------------------------------------------------------------
  // panel-desktop: width + resize state (always called — hooks must be
  // unconditional even when this surface isn't currently rendered).
  // -----------------------------------------------------------------------

  const {
    width,
    isDragging,
    contentRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
  } = useResizablePanelWidth({ open, defaultWidth, minWidth, storageKey });

  // -----------------------------------------------------------------------
  // mobile: detent drag handlers (always called; enabled conditionally)
  // -----------------------------------------------------------------------

  const dragHandlers = useSheetDrag({
    onDismiss: () => handleOpenChange(false),
    enabled: isMobile && !preventDismiss,
    resetKey: mobileDragResetKey,
  });

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (variant === 'confirm') {
    if (isMobile) {
      return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <SheetContent
            side="bottom"
            {...restContentProps}
            className={cn(
              'max-h-[90dvh] rounded-t-xl p-0 flex flex-col overflow-hidden',
              'motion-reduce:transition-none motion-reduce:animate-none',
              mobileClassName,
              className,
              contentPropsClassName,
            )}
            onEscapeKeyDown={onEscapeKeyDown}
            onInteractOutside={onInteractOutside}
            {...(description == null && srDescription == null
              ? { 'aria-describedby': undefined }
              : {})}
          >
            <GLSheetGrabHandle
              ref={dragHandlers.handleRef}
              onPointerDown={dragHandlers.onPointerDown}
              onPointerMove={dragHandlers.onPointerMove}
              onPointerUp={dragHandlers.onPointerUp}
              onPointerCancel={dragHandlers.onPointerCancel}
            />
            <SheetHeader className="px-5 pt-3 pb-4 border-b border-border">
              <SheetTitle>{title}</SheetTitle>
              {description != null ? (
                <SheetDescription>{description}</SheetDescription>
              ) : srDescription != null ? (
                <SheetDescription className="sr-only">{srDescription}</SheetDescription>
              ) : null}
            </SheetHeader>
            <div className={cn('flex-1 overflow-y-auto px-5 py-4', bodyClassName)}>{children}</div>
            {footer != null && (
              <SheetFooter className="border-t border-border px-5 py-4 pb-safe">
                {footer}
              </SheetFooter>
            )}
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          {...restContentProps}
          className={cn('sm:max-w-md', desktopClassName, className, contentPropsClassName)}
          onEscapeKeyDown={onEscapeKeyDown}
          onInteractOutside={onInteractOutside}
          {...(description == null && srDescription == null
            ? { 'aria-describedby': undefined }
            : {})}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description != null ? (
              <DialogDescription>{description}</DialogDescription>
            ) : srDescription != null ? (
              <DialogDescription className="sr-only">{srDescription}</DialogDescription>
            ) : null}
          </DialogHeader>
          <div className={cn('py-2', bodyClassName)}>{children}</div>
          {footer != null && <DialogFooter>{footer}</DialogFooter>}
        </DialogContent>
      </Dialog>
    );
  }

  // variant === 'panel'

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          {...restContentProps}
          className={cn(
            'max-h-[90dvh] rounded-t-xl p-0 flex flex-col overflow-hidden',
            'motion-reduce:transition-none motion-reduce:animate-none',
            mobileClassName,
            className,
            contentPropsClassName,
          )}
          onEscapeKeyDown={onEscapeKeyDown}
          onInteractOutside={onInteractOutside}
          // When both description and srDescription are absent, opt out of
          // Radix's aria-describedby to silence its advisory.
          {...(description == null && srDescription == null
            ? { 'aria-describedby': undefined }
            : {})}
        >
          {/*
           * Grab handle must be a direct child of SheetContent (which renders
           * SheetPrimitive.Content) so useSheetDrag's parentElement traversal
           * reaches the actual panel DOM node.
           */}
          <GLSheetGrabHandle
            ref={dragHandlers.handleRef}
            onPointerDown={dragHandlers.onPointerDown}
            onPointerMove={dragHandlers.onPointerMove}
            onPointerUp={dragHandlers.onPointerUp}
            onPointerCancel={dragHandlers.onPointerCancel}
          />
          <SheetHeader className="px-5 pt-5 pb-4 border-b border-border">
            <SheetTitle asChild>
              <div>{title}</div>
            </SheetTitle>
            {description != null ? (
              <SheetDescription>{description}</SheetDescription>
            ) : srDescription != null ? (
              <SheetDescription className="sr-only">{srDescription}</SheetDescription>
            ) : null}
          </SheetHeader>
          <div className={cn('flex-1 overflow-y-auto px-5 py-4', bodyClassName)}>{children}</div>
          {footer != null && (
            <SheetFooter className="border-t border-border px-5 py-4 pb-safe">{footer}</SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  // panel + desktop: resizable right panel
  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetPortal>
        <SheetOverlay />
        {/*
         * Use SheetPrimitive.Content directly to own the close button and
         * avoid a duplicate. Animation classes from sheetVariants are applied
         * manually.
         */}
        <SheetPrimitive.Content
          {...restContentProps}
          ref={contentRef}
          tabIndex={-1}
          style={{ width, maxWidth: '94vw' }}
          {...(description == null && srDescription == null
            ? { 'aria-describedby': undefined }
            : {})}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            contentRef.current?.focus();
          }}
          onEscapeKeyDown={onEscapeKeyDown}
          onInteractOutside={onInteractOutside}
          className={cn(
            'focus:outline-none',
            'fixed z-50 gap-4 bg-background shadow-lg transition ease-in-out',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:duration-300 data-[state=open]:duration-500',
            'inset-y-0 right-0 h-full border-l',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            '!max-w-none w-auto',
            'flex flex-col p-0 overflow-hidden',
            'motion-reduce:transition-none motion-reduce:animate-none',
            desktopClassName,
            className,
            contentPropsClassName,
          )}
        >
          <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <div className="flex items-start justify-between gap-4 pr-2">
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-base font-semibold text-foreground">
                  {title}
                </DialogPrimitive.Title>
                {description != null ? (
                  <DialogPrimitive.Description className="mt-1 text-sm text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                ) : srDescription != null ? (
                  <DialogPrimitive.Description className="sr-only">
                    {srDescription}
                  </DialogPrimitive.Description>
                ) : null}
              </div>
              <SheetClose
                aria-label="Close"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </SheetClose>
            </div>
          </SheetHeader>

          <div className={cn('flex-1 overflow-y-auto px-6 py-4 min-h-0', bodyClassName)}>
            {children}
          </div>

          {footer != null && (
            <div className="shrink-0 border-t border-border px-6 py-4">{footer}</div>
          )}

          {/*
           * Resize handle — rendered last so it isn't the initial focus target
           * when the panel opens.
           */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onKeyDown={handleKeyDown}
            className="group/resize absolute inset-y-0 left-0 z-10 flex w-2.5 cursor-col-resize items-center justify-center focus-visible:outline-none"
          >
            <span
              aria-hidden="true"
              className={cn(
                'h-8 w-1 rounded-full transition-colors',
                isDragging
                  ? 'bg-slate/60'
                  : 'bg-border group-hover/resize:bg-slate/40 group-focus-visible/resize:bg-slate/40',
              )}
            />
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}
